import { Vec2, Vec4 } from 'features/assetViz/utils/helpers'
import { overlayMaterialDefines } from 'features/assetViz/utils/renderingDefines'
import { useCdfRawValues } from 'features/dataLayers'
import React, { useEffect, useMemo } from 'react'
import * as THREE from 'three'
import { GeckoComponentDataFrame, GeckoComponentRect } from 'types'

import vert from './surface.vert'
import frag from './textData.frag'

// For live-reload of the shader file on save.
const key = THREE.MathUtils.generateUUID()

/**
 * The material used to render text on the component surface.
 * @param chunkBounds     The bounds of the chunk, in component coordinates
 * @param doWrapX         Whether this chunk wraps in the x direction
 * @param doWrapY         Whether this chunk wraps in the y direction
 * @param cdf             The CDF to center the grid on
 * @constructor
 */
export const TextDataMaterial: React.FC<{
  chunkBounds: GeckoComponentRect
  doWrapX: boolean
  doWrapY: boolean
  cdf: GeckoComponentDataFrame
}> = ({ chunkBounds, doWrapX, doWrapY, cdf }) => {
  //----------------------------------------------------------------------------

  // An object of measurement to pass down to the shader level. Note that this
  // object should never be recreated; only its measurement should be updated
  const uniforms = React.useMemo(
    () => ({
      u_tex: { value: null as THREE.Texture | null },
      u_tex_rect: { value: [0, 0, 0, 0] as Vec4 },
      u_chunk_rect: { value: [0, 0, 0, 0] as Vec4 },
      u_wrap_component: { value: [false, false] },
      u_wrap_dimensions: { value: [0, 0] as Vec2 },
      u_bin_size: { value: [1, 1] as Vec2 },
    }),
    []
  )

  //----------------------------------------------------------------------------

  const width = cdf.data.binning.x_bin_count
  const height = cdf.data.binning.y_bin_count

  // Only allocate a new buffer or image if the size of the data changes
  const { cdfDataBuffer, cdfTexture } = useMemo(() => {
    const cdfDataBuffer = new Float32Array(width * height)
    const cdfTexture = new THREE.DataTexture(
      cdfDataBuffer,
      width,
      height,
      THREE.RedFormat,
      THREE.FloatType
    )
    cdfTexture.wrapS = THREE.ClampToEdgeWrapping
    cdfTexture.wrapT = THREE.ClampToEdgeWrapping
    cdfTexture.internalFormat = 'R32F'
    return { cdfDataBuffer, cdfTexture }
  }, [width, height])

  const { buffer: binnedDataBuffer, isLoading: isLoadingCdfData } =
    useCdfRawValues(cdf)

  // Whenever the data loads in update the array buffer and tell ThreeJS that
  // the data texture needs updating
  useEffect(() => {
    const textureSize = cdfTexture.image.width * cdfTexture.image.height
    cdfDataBuffer[textureSize - 1] = Number.NaN
    // TODO: If we move to a Float32Array rather than a number[], then cdfDataBuffer.set() will work
    // TODO: remove once CDF is fully generated in front end
    const cdfDataLength = (binnedDataBuffer?.values ?? []).length
    if (cdfDataLength !== textureSize) {
      console.error(`Mismatch between bin counts and size of the array attached to the CDF \n
                    x bin count: ${cdfTexture.image.width} \n
                    y bin count: ${cdfTexture.image.height} \n
                    cdf array length: ${cdfDataLength}`)
      cdfDataBuffer.set([])
    } else {
      cdfDataBuffer.set(binnedDataBuffer?.values ?? [])
    }
    cdfTexture.needsUpdate = true
    cdfTexture.generateMipmaps = false
  }, [cdfDataBuffer, cdfTexture, binnedDataBuffer, isLoadingCdfData])

  React.useEffect(() => {
    if (!cdfTexture) return

    cdfTexture.generateMipmaps = false
    cdfTexture.wrapS = THREE.ClampToEdgeWrapping
    cdfTexture.wrapT = THREE.ClampToEdgeWrapping
    cdfTexture.magFilter = THREE.NearestFilter
    cdfTexture.minFilter = THREE.NearestFilter
    cdfTexture.needsUpdate = true

    const cdfGeometry = cdf.data.geometry.convertTo(
      chunkBounds.x.unit,
      chunkBounds.y.unit
    )
    const xUnit = chunkBounds.x.unit
    const yUnit = chunkBounds.y.unit
    uniforms.u_wrap_dimensions.value = [
      doWrapX ? chunkBounds.width.num : -1,
      doWrapY ? chunkBounds.height.num : -1,
    ]
    const convertedBinSizes = cdf.data.geometry.convertTo(xUnit, yUnit)
    uniforms.u_chunk_rect.value = [
      chunkBounds.start.x.num - convertedBinSizes.start.x.num,
      chunkBounds.start.y.num - convertedBinSizes.start.y.num,
      chunkBounds.end.x.num - convertedBinSizes.start.x.num,
      chunkBounds.end.y.num - convertedBinSizes.start.y.num,
    ]
    uniforms.u_tex_rect.value = [
      cdfGeometry.start.x.num,
      cdfGeometry.start.y.num,
      cdfGeometry.end.x.num,
      cdfGeometry.end.y.num,
    ]
    uniforms.u_tex.value = cdfTexture
    uniforms.u_wrap_component.value = [doWrapX, doWrapY]

    const binWidth = cdf.data.binning.bin_size.width.convertTo(xUnit).num
    const binHeight = cdf.data.binning.bin_size.height.convertTo(yUnit).num
    uniforms.u_bin_size.value = [binWidth, binHeight]
  }, [uniforms, chunkBounds, cdf, doWrapX, doWrapY, cdfTexture])

  //----------------------------------------------------------------------------

  return (
    <shaderMaterial
      key={key}
      vertexShader={vert}
      fragmentShader={frag}
      uniforms={uniforms}
      {...overlayMaterialDefines}
    />
  )
}
